import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Second line / trailing hint — a domain, a code, anything disambiguating. */
  hint?: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Accessible name for the trigger. */
  label: string;
  disabled?: boolean;
  /**
   * Open the menu upwards. For controls living in a strip at the BOTTOM of the
   * shell, where a menu hanging below would open off-screen.
   */
  dropUp?: boolean;
  /** Trigger width. Defaults to `w-64`. */
  className?: string;
}

/**
 * A select you can type into.
 *
 * <p>
 * Exists because a native <c>&lt;select&gt;</c> stops being a choice and becomes
 * a scroll somewhere around fifty options — and the support strips pick from
 * every account on the platform, which is heading for thousands. Everywhere the
 * list is short and fixed, the native control is still the better answer.
 * </p>
 *
 * <p>
 * Dark by default: both its callers are strips on a dark ground. It is styled
 * for that rather than pretending to be theme-neutral and being wrong in both.
 * </p>
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  label,
  disabled = false,
  dropUp = false,
  className,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click, as AccountSwitcher does.
  useEffect(() => {
    if (!isOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  // Reset the query on close, so reopening never starts mid-search from
  // whatever was typed minutes ago.
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={cn('relative', className ?? 'w-64')} ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        disabled={disabled || options.length === 0}
        onClick={() => setIsOpen((o) => !o)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded border border-white/20 bg-white/10',
          'px-2 py-1 text-left text-sm text-white hover:border-white/40',
          'disabled:cursor-default disabled:opacity-50',
        )}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-50 w-80 overflow-hidden rounded-md border border-slate-700 bg-slate-800 shadow-lg',
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          <div className="relative border-b border-slate-700 p-2">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full rounded border border-slate-600 bg-slate-900 py-1 pl-7 pr-2 text-sm text-white outline-none focus:border-slate-400"
            />
          </div>

          <ul role="listbox" aria-label={label} className="max-h-72 overflow-auto py-1">
            {visible.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-400">No matches.</li>
            )}
            {visible.map((o) => {
              const active = o.value === value;
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      setIsOpen(false);
                      onChange(o.value);
                    }}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-white hover:bg-slate-700"
                  >
                    <span className="truncate">
                      {o.label}
                      {o.hint && <span className="ml-2 text-xs text-slate-400">{o.hint}</span>}
                    </span>
                    {active && <Check className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
